import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { agentCardsAPI, authAPI, crmAPI, legalAPI } from "../api";
import { getUserErrorMessage } from "../utils/errors";
import MIcon from "../icons/MIcon";
import MessageBanner from "../components/ui/MessageBanner";
import SpinnerIcon from "../icons/SpinnerIcon";
import Toggle from "../components/ui/Toggle";
import { agentConfig } from "../constants/agentConfig";

const WizardStepper = ({ steps, currentStep }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-1 mb-8">
      {steps.map((step, idx) => {
        const isActive = idx === currentStep;
        const isDone = idx < currentStep;
        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div
                className={`h-px w-6 sm:w-10 transition-colors duration-300 ${isDone ? "bg-brand" : "bg-surface-light-highest dark:bg-white/10"}`}
              />
            )}
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`
                w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300
                ${
                  isActive
                    ? "bg-brand text-white shadow-brand-glow"
                    : isDone
                      ? "bg-brand/20 dark:bg-brand/30 text-brand"
                      : "bg-surface-light-container dark:bg-surface-container-high text-on-surface-light-variant dark:text-on-surface-variant"
                }
              `}
              >
                {isDone ? (
                  <MIcon name="check" className="!text-[16px]" />
                ) : (
                  idx + 1
                )}
              </div>
              <span
                className={`text-[10px] font-medium hidden sm:block whitespace-nowrap ${isActive ? "text-brand" : "text-on-surface-light-variant dark:text-on-surface-variant"}`}
              >
                {t(`agents.steps.${step.key}`)}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const DeleteConfirmModal = ({
  agentOrgName,
  onConfirm,
  onCancel,
  deleting,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn"
      onClick={onCancel}
    >
      <div
        className="bg-surface-light-lowest dark:bg-surface-container border border-surface-light-highest dark:border-white/5 rounded-xl max-w-md w-full shadow-2xl animate-slideUp p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/10 dark:bg-red-400/10 flex items-center justify-center">
            <MIcon name="warning" className="text-red-500 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-on-surface-light dark:text-on-surface">
            {t("agents.delete.title")}
          </h3>
        </div>
        <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant mb-6">
          {t("agents.delete.confirmPrefix")}{" "}
          <strong className="text-on-surface-light dark:text-on-surface">
            {agentOrgName}
          </strong>
          {t("agents.delete.confirmSuffix")}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2.5 bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 text-on-surface-light dark:text-on-surface rounded-lg hover:bg-surface-light-high dark:hover:bg-surface-bright transition-colors font-medium text-sm"
          >
            {t("agents.cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2.5 bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-500 text-white rounded-lg transition-colors font-semibold text-sm flex items-center gap-2 disabled:opacity-40"
          >
            {deleting ? (
              <SpinnerIcon />
            ) : (
              <MIcon name="delete" className="sm" />
            )}
            <span>
              {deleting ? t("agents.deleting") : t("agents.delete.confirm")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Agents = () => {
  const { t, i18n } = useTranslation();

  // Format a duration (in minutes) into a localised label.
  const formatDuration = (m) => {
    const h = Math.floor(m / 60);
    const mins = m % 60;
    if (h === 0) return t("agents.duration.minutes", { count: mins });
    if (mins === 0) return t("agents.duration.hours", { count: h });
    return t("agents.duration.hoursMinutes", { hours: h, minutes: mins });
  };

  // Agent cards list
  const [agents, setAgents] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wizardMsg, setWizardMsg] = useState({ text: "", type: "" });

  // Wizard form data
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    ...agentConfig.DEFAULT_SCHEDULE,
  });
  const [appointmentType, setAppointmentType] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [sendSms, setSendSms] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState("");
  const [mandatoryPhrase, setMandatoryPhrase] = useState("");
  const [voice, setVoice] = useState("");
  const [sttProvider, setSttProvider] = useState(
    agentConfig.STT_PROVIDER_OPTIONS[0].value,
  );
  const [sttModel, setSttModel] = useState(
    agentConfig.STT_MODEL_OPTIONS[0].value,
  );
  const [ttsProvider, setTtsProvider] = useState(
    agentConfig.TTS_PROVIDER_OPTIONS[0].value,
  );
  const [ttsModel, setTtsModel] = useState(
    agentConfig.TTS_MODEL_OPTIONS[0].value,
  );
  const [llmModel, setLlmModel] = useState(
    agentConfig.LLM_MODEL_OPTIONS[0].value,
  );

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const appointmentsEnabled =
    appointmentType === "inbound" || appointmentType === "outbound";

  // ── Load Data ──

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsRes, orgsRes, legalRes] = await Promise.all([
        agentCardsAPI.getAll(),
        authAPI.getMyOrgs(),
        legalAPI.get(),
      ]);
      setAgents(agentsRes.data);
      setOrgs(orgsRes.data);
      setMandatoryPhrase(legalRes.data.text);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Eroare la încărcarea datelor:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Helpers ──

  const getOrgName = (orgId) => {
    const org = orgs.find((o) => o.id === orgId);
    return org ? org.orgname : t("agents.orgFallback", { id: orgId });
  };

  const resetWizard = () => {
    setWizardStep(0);
    setEditingAgentId(null);
    setSelectedOrgId(orgs.length === 1 ? orgs[0].id : null);
    setSelectedOrg(orgs.length === 1 ? orgs[0] : null);
    setScheduleEnabled(false);
    setScheduleData({ ...agentConfig.DEFAULT_SCHEDULE });
    setAppointmentType(null);
    setDurationMinutes(30);
    setSendSms(false);
    setGreetingMessage("");
    setVoice("");
    setSttProvider(agentConfig.STT_PROVIDER_OPTIONS[0].value);
    setSttModel(agentConfig.STT_MODEL_OPTIONS[0].value);
    setTtsProvider(agentConfig.TTS_PROVIDER_OPTIONS[0].value);
    setTtsModel(agentConfig.TTS_MODEL_OPTIONS[0].value);
    setLlmModel(agentConfig.LLM_MODEL_OPTIONS[0].value);
    setWizardMsg({ text: "", type: "" });
  };

  // When org is selected, look up the full org object and pre-fill greeting
  const handleOrgSelect = (orgId) => {
    const org = orgs.find((o) => o.id === orgId);
    setSelectedOrgId(orgId);
    setSelectedOrg(org || null);
    if (org && !greetingMessage.trim()) {
      setGreetingMessage(agentConfig.buildGreeting(org));
    }
  };

  // ── Open wizard (new or edit) ──

  const openCreateWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const openEditWizard = async (agent) => {
    resetWizard();
    setEditingAgentId(agent.id);
    setSelectedOrgId(agent.org_id);
    setVoice(agent.voice || "");
    if (agent.stt_provider) setSttProvider(agent.stt_provider);
    if (agent.stt_model) setSttModel(agent.stt_model);
    if (agent.tts_provider) setTtsProvider(agent.tts_provider);
    if (agent.tts_model) setTtsModel(agent.tts_model);
    if (agent.llm_model) {
      const hasProvider =
        agent.llm_model.startsWith("llm_proxy/") ||
        agent.llm_model.startsWith("live_inference/") ||
        agent.llm_model.startsWith("cloud_avenue/");
      const explicitModel = hasProvider
        ? agent.llm_model
        : `llm_proxy/${agent.llm_model}`;
      const isSelectable = agentConfig.LLM_MODEL_OPTIONS.some(
        (option) => option.value === explicitModel,
      );
      setLlmModel(isSelectable ? explicitModel : agent.llm_model);
    }

    // Look up org details from already-loaded orgs list
    const org = orgs.find((o) => o.id === agent.org_id);
    setSelectedOrg(org || null);

    // Load CRM config for this org
    try {
      const res = await crmAPI.getConfig(agent.org_id);
      const { schedule, appointments } = res.data;

      const existingGreeting = agent.greeting_message || "";
      setGreetingMessage(
        existingGreeting || (org ? agentConfig.buildGreeting(org) : ""),
      );
      if (schedule) {
        setScheduleEnabled(schedule.enabled);
        setScheduleData(
          schedule.schedule_data || { ...agentConfig.DEFAULT_SCHEDULE },
        );
      }
      if (appointments) {
        setAppointmentType(
          appointments.enabled
            ? appointments.config?.type || "inbound"
            : "none",
        );
        setDurationMinutes(appointments.duration_minutes || 30);
        setSendSms(appointments.send_sms_confirmation || false);
      }
    } catch {
      // Use defaults
    }

    setWizardOpen(true);
  };

  const canGoNext = () => {
    switch (wizardStep) {
      case 0: // Org
        return !!selectedOrgId;
      case 1: // Schedule
        if (scheduleEnabled) {
          return Object.values(scheduleData).some((d) => d.enabled);
        }
        return true;
      case 2: // Appointments
        return appointmentType !== null;
      case 3: // Greeting + models
        return (
          !!voice &&
          !!sttProvider &&
          !!sttModel &&
          !!ttsProvider &&
          !!ttsModel &&
          !!llmModel
        );
      default:
        return true;
    }
  };

  const handleNext = () => {
    setWizardMsg({ text: "", type: "" });
    // When leaving the org step, pre-fill the greeting if empty
    if (wizardStep === 0 && !greetingMessage.trim() && selectedOrg) {
      setGreetingMessage(agentConfig.buildGreeting(selectedOrg));
    }
    if (wizardStep < agentConfig.WIZARD_STEPS.length - 1) {
      setWizardStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    setWizardMsg({ text: "", type: "" });
    if (wizardStep > 0) {
      setWizardStep((s) => s - 1);
    }
  };

  const handleCreateAgent = async () => {
    setWizardMsg({ text: "", type: "" });
    setSaving(true);

    try {
      // 1. Save CRM Basic (derived from org details)
      if (selectedOrg) {
        await crmAPI.updateBasic(selectedOrgId, {
          company_name: selectedOrg.orgname || "",
          business_type: selectedOrg.org_type || "",
          address: selectedOrg.address || "",
          phone: selectedOrg.phone || "",
          email: selectedOrg.email || "",
        });
      }

      // 2. Save CRM Schedule
      await crmAPI.updateSchedule(selectedOrgId, {
        enabled: scheduleEnabled,
        schedule_data: scheduleData,
      });

      // 3. Save CRM Appointments
      // When services drive the duration (services_config.mandatory_duration),
      // the duration dropdown is hidden and the slot duration comes from each
      // service, so we persist a null duration_minutes.
      const servicesDriveDuration = !!selectedOrg?.services_config
        ?.mandatory_duration;
      await crmAPI.updateAppointments(selectedOrgId, {
        enabled: appointmentsEnabled,
        duration_minutes:
          appointmentsEnabled && !servicesDriveDuration
            ? durationMinutes
            : null,
        send_sms_confirmation: appointmentsEnabled ? sendSms : false,
        config: appointmentsEnabled ? { type: appointmentType } : null,
      });

      // 4. Upsert AgentCard — store only the raw greeting. The mandatory
      // legal phrase is appended server-side when the agent config is built.
      await agentCardsAPI.upsert({
        org_id: selectedOrgId,
        greeting_message: greetingMessage.trim(),
        voice: voice,
        stt_provider: sttProvider,
        stt_model: sttModel,
        tts_provider: ttsProvider,
        tts_model: ttsModel,
        llm_model: llmModel,
      });

      setWizardOpen(false);
      resetWizard();
      await loadData();
    } catch (err) {
      setWizardMsg({
        text: getUserErrorMessage(err, "save"),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await agentCardsAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Eroare la ștergere:", err);
      }
    } finally {
      setDeleting(false);
    }
  };

  const updateDay = (dayKey, field, value) => {
    setScheduleData((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value },
    }));
  };

  const renderStepContent = () => {
    switch (wizardStep) {
      case 0: // Org selector
        return (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface mb-1">
              {t("agents.step.org.title")}
            </h2>
            <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-4">
              {t("agents.step.org.subtitle")}
            </p>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none z-10">
                <MIcon name="corporate_fare" className="sm" />
              </div>
              <select
                value={selectedOrgId || ""}
                onChange={(e) => handleOrgSelect(parseInt(e.target.value))}
                className="auth-input glass-select w-full pl-11 pr-10 py-3 rounded-lg text-sm font-body appearance-none cursor-pointer"
              >
                <option value="" disabled>
                  {t("agents.step.org.title")}
                </option>
                {orgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.orgname} ({org.phone})
                  </option>
                ))}
              </select>
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                <MIcon name="expand_more" className="sm" />
              </div>
            </div>

            {/* Org details summary */}
            {selectedOrg && (
              <div className="mt-4 bg-surface-light-low dark:bg-surface-container-low border border-surface-light-highest dark:border-white/5 rounded-lg p-4 space-y-2 text-sm text-on-surface-light-variant dark:text-on-surface-variant">
                <div className="flex items-center gap-2 text-on-surface-light dark:text-on-surface font-medium">
                  <MIcon name="apartment" className="sm" />
                  <span>{selectedOrg.orgname}</span>
                  {selectedOrg.org_type && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 dark:bg-brand/20 text-brand dark:text-brand-light">
                      {t(`organizations.orgTypes.${selectedOrg.org_type}`)}
                    </span>
                  )}
                </div>
                {selectedOrg.phone && (
                  <div className="flex items-center gap-2">
                    <MIcon name="call" className="sm opacity-60" />
                    <span>{selectedOrg.phone}</span>
                  </div>
                )}
                {selectedOrg.email && (
                  <div className="flex items-center gap-2">
                    <MIcon name="mail" className="sm opacity-60" />
                    <span>{selectedOrg.email}</span>
                  </div>
                )}
                {selectedOrg.address && (
                  <div className="flex items-center gap-2">
                    <MIcon name="location_on" className="sm opacity-60" />
                    <span>{selectedOrg.address}</span>
                  </div>
                )}
                {selectedOrg.services && selectedOrg.services.length > 0 && (
                  <div className="flex items-start gap-2">
                    <MIcon name="handyman" className="sm opacity-60 mt-0.5" />
                    <span>
                      {selectedOrg.services.map((s) => s.name).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 1: // Schedule
        return (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface mb-1">
              {t("agents.step.schedule.title")}
            </h2>
            <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-4">
              {t("agents.step.schedule.subtitle")}
            </p>

            <Toggle
              enabled={scheduleEnabled}
              onChange={setScheduleEnabled}
              label={t("agents.step.schedule.toggle")}
            />

            {scheduleEnabled && (
              <div className="mt-4 space-y-3">
                {agentConfig.DAYS.map((key) => {
                  const label = t(`agents.days.${key}`);
                  const day = scheduleData[key] || {
                    enabled: false,
                    start_time: "10:00",
                    end_time: "20:00",
                  };
                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all duration-200 ${
                        day.enabled
                          ? "bg-surface-light-low dark:bg-surface-container-low border border-surface-light-highest dark:border-white/5"
                          : "opacity-50"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => updateDay(key, "enabled", !day.enabled)}
                        className={`toggle-switch flex-shrink-0 ${day.enabled ? "active" : ""}`}
                        style={{ width: "36px", height: "20px" }}
                        aria-label={t("agents.step.schedule.toggleDay", {
                          day: label,
                        })}
                      />
                      <span className="text-sm text-on-surface-light dark:text-on-surface font-medium w-24 flex-shrink-0">
                        {label}
                      </span>
                      {day.enabled && (
                        <div className="flex items-center gap-2 flex-1">
                          <select
                            value={day.start_time}
                            onChange={(e) =>
                              updateDay(key, "start_time", e.target.value)
                            }
                            className="time-select"
                          >
                            {agentConfig.TIME_OPTIONS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                          <span className="text-on-surface-light-variant dark:text-on-surface-variant text-xs">
                            —
                          </span>
                          <select
                            value={day.end_time}
                            onChange={(e) =>
                              updateDay(key, "end_time", e.target.value)
                            }
                            className="time-select"
                          >
                            {agentConfig.TIME_OPTIONS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 2: { // Appointments
        const appointmentOptions = [
          { value: "none", icon: "event_busy" },
          { value: "inbound", icon: "storefront" },
          { value: "outbound", icon: "home_pin" },
        ];

        return (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface mb-1">
              {t("agents.step.appointments.title")}
            </h2>
            <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-4">
              {t("agents.step.appointments.subtitle")}
            </p>

            <div
              className="grid grid-cols-3 gap-2"
              role="radiogroup"
              aria-label={t("agents.step.appointments.locationLabel")}
            >
              {appointmentOptions.map((option) => {
                const isSelected = appointmentType === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setAppointmentType(option.value)}
                    className={`min-h-24 rounded-xl border px-2.5 py-3 flex flex-col items-center justify-center gap-2 text-center transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-light-lowest dark:focus-visible:ring-offset-surface-container ${
                      isSelected
                        ? "border-brand bg-brand text-white shadow-brand-glow"
                        : "border-surface-light-highest dark:border-white/10 bg-surface-light-low dark:bg-surface-container-low text-on-surface-light dark:text-on-surface hover:border-brand/50 hover:bg-brand/5 dark:hover:bg-brand/10"
                    }`}
                  >
                    <MIcon
                      name={option.icon}
                      filled={isSelected}
                      className="!text-[22px]"
                    />
                    <span className="text-xs sm:text-sm font-semibold leading-tight">
                      {t(
                        `agents.step.appointments.options.${option.value}`,
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {appointmentsEnabled && (
              <div className="mt-4 space-y-5">
                <div className="flex items-start gap-3 rounded-xl border border-brand/25 dark:border-brand/30 bg-brand/5 dark:bg-brand/10 px-4 py-3 text-sm text-on-surface-light dark:text-on-surface">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/15 dark:bg-brand/20 text-brand">
                    <MIcon name="location_on" className="!text-[17px]" />
                  </div>
                  <span className="leading-relaxed">
                    {appointmentType === "inbound"
                      ? t("agents.step.appointments.businessLocation", {
                          address: selectedOrg?.address || "—",
                        })
                      : t("agents.step.appointments.clientLocation")}
                  </span>
                </div>

                {selectedOrg?.services_config?.mandatory_duration ? (
                  <div className="flex items-start gap-2 rounded-lg border border-surface-light-highest dark:border-white/10 bg-surface-light-container/50 dark:bg-surface-container/50 px-3 py-2 text-xs text-on-surface-light-variant dark:text-on-surface-variant">
                    <MIcon
                      name="info"
                      className="sm mt-0.5 shrink-0 opacity-70"
                    />
                    <span>
                      {t("agents.step.appointments.durationFromServices")}
                    </span>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                      {t("agents.step.appointments.duration")}
                    </label>
                    <div className="relative w-full sm:w-64">
                      <select
                        value={durationMinutes}
                        onChange={(e) =>
                          setDurationMinutes(parseInt(e.target.value))
                        }
                        className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                      >
                        {agentConfig.DURATION_OPTIONS.map((value) => (
                          <option key={value} value={value}>
                            {formatDuration(value)}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                        <MIcon name="expand_more" className="sm" />
                      </div>
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                    className="crm-checkbox"
                  />
                  <span className="text-sm text-on-surface-light dark:text-on-surface font-medium">
                    {t("agents.step.appointments.sendSms")}
                  </span>
                </label>
              </div>
            )}
          </div>
        );
      }

      case 3: // Greeting message
        return (
          <div className="space-y-4">
            <h2 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface mb-1">
              {t("agents.step.greeting.title")}
            </h2>
            <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-4">
              {t("agents.step.greeting.subtitle")}
            </p>

            <div>
              <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                {t("agents.step.greeting.yourMessage")}
              </label>
              <textarea
                value={greetingMessage}
                onChange={(e) => setGreetingMessage(e.target.value)}
                placeholder={t("agents.step.greeting.placeholder")}
                rows={2}
                className="auth-input w-full px-4 py-2.5 rounded-lg text-sm font-body resize-none"
              />
            </div>

            <div className="mt-4">
              <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                {t("agents.step.greeting.finalMessage")}
              </label>
              <div className="bg-surface-light-low dark:bg-surface-container-low border border-surface-light-highest dark:border-white/5 rounded-lg p-4 text-sm text-on-surface-light dark:text-on-surface leading-relaxed">
                {greetingMessage.trim() ? (
                  <>
                    <span>
                      {greetingMessage.trim().endsWith(".")
                        ? greetingMessage.trim()
                        : `${greetingMessage.trim()}.`}
                    </span>{" "}
                    <span>{mandatoryPhrase}</span>
                  </>
                ) : (
                  <span>{mandatoryPhrase}</span>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                {t("agents.voice.label")}
              </label>
              <div className="relative w-full">
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                >
                  <option value="" disabled>
                    {t("agents.voice.placeholder")}
                  </option>
                  {agentConfig.VOICE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                  <MIcon name="expand_more" className="sm" />
                </div>
              </div>
            </div>

            {/* ── Provider / model selection ── */}
            <div className="mt-6 pt-6 border-t border-surface-light-highest dark:border-white/5">
              <h3 className="font-display text-sm font-semibold text-on-surface-light dark:text-on-surface mb-3">
                {t("agents.models.title")}
              </h3>
              <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-4">
                {t("agents.models.subtitle")}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* STT provider */}
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("agents.models.sttProvider")}
                  </label>
                  <div className="relative w-full">
                    <select
                      value={sttProvider}
                      onChange={(e) => setSttProvider(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {agentConfig.STT_PROVIDER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                      <MIcon name="expand_more" className="sm" />
                    </div>
                  </div>
                </div>

                {/* STT model */}
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("agents.models.sttModel")}
                  </label>
                  <div className="relative w-full">
                    <select
                      value={sttModel}
                      onChange={(e) => setSttModel(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {agentConfig.STT_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                      <MIcon name="expand_more" className="sm" />
                    </div>
                  </div>
                </div>

                {/* TTS provider */}
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("agents.models.ttsProvider")}
                  </label>
                  <div className="relative w-full">
                    <select
                      value={ttsProvider}
                      onChange={(e) => setTtsProvider(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {agentConfig.TTS_PROVIDER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                      <MIcon name="expand_more" className="sm" />
                    </div>
                  </div>
                </div>

                {/* TTS model */}
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("agents.models.ttsModel")}
                  </label>
                  <div className="relative w-full">
                    <select
                      value={ttsModel}
                      onChange={(e) => setTtsModel(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {agentConfig.TTS_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                      <MIcon name="expand_more" className="sm" />
                    </div>
                  </div>
                </div>

                {/* LLM model */}
                <div className="sm:col-span-2">
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("agents.models.llmModel")}
                  </label>
                  <div className="relative w-full">
                    <select
                      value={llmModel}
                      onChange={(e) => setLlmModel(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {agentConfig.LLM_MODEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                      <MIcon name="expand_more" className="sm" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = wizardStep === agentConfig.WIZARD_STEPS.length - 1;

  return (
    <div className="min-h-screen font-body">
      {/* Header */}
      <header className="bg-surface-light-lowest dark:bg-[#121212] border-b border-surface-light-highest dark:border-white/5 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight">
                {t("nav.agents")}
              </h1>
              <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant mt-0.5">
                {t("agents.subtitle")}
              </p>
            </div>
            <button
              onClick={openCreateWizard}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white rounded-lg shadow-brand-glow transition-all font-semibold text-sm active:scale-95"
            >
              <MIcon name="add_circle" className="sm" />
              <span>{t("agents.createAgent")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <SpinnerIcon />
          </div>
        ) : agents.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-surface-light-container dark:bg-surface-container-high rounded-full flex items-center justify-center mx-auto mb-4">
              <MIcon
                name="smart_toy"
                className="!text-[40px] text-on-surface-light-variant dark:text-on-surface-variant"
              />
            </div>
            <h3 className="text-lg font-semibold text-on-surface-light dark:text-on-surface mb-2">
              {t("agents.empty")}
            </h3>
            <p className="text-on-surface-light-variant dark:text-on-surface-variant text-sm mb-6">
              {t("agents.emptyHint")}
            </p>
            <button
              onClick={openCreateWizard}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white rounded-lg shadow-brand-glow transition-all font-semibold text-sm active:scale-95"
            >
              <MIcon name="add_circle" className="sm" />
              <span>{t("agents.createAgent")}</span>
            </button>
          </div>
        ) : (
          /* Agent Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="group flat-card rounded-xl p-6 hover:border-outline-light-variant dark:hover:border-outline-variant transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-brand/10 dark:bg-brand/15 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MIcon
                      name="smart_toy"
                      className="text-brand !text-[24px]"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditWizard(agent)}
                      className="w-8 h-8 rounded-lg hover:bg-surface-light-container dark:hover:bg-surface-container-high flex items-center justify-center transition-colors text-on-surface-light-variant dark:text-on-surface-variant hover:text-brand"
                      title={t("agents.edit")}
                    >
                      <MIcon name="edit" className="!text-[18px]" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(agent)}
                      className="w-8 h-8 rounded-lg hover:bg-red-500/10 dark:hover:bg-red-400/10 flex items-center justify-center transition-colors text-on-surface-light-variant dark:text-on-surface-variant hover:text-red-500 dark:hover:text-red-400"
                      title={t("agents.delete.confirm")}
                    >
                      <MIcon name="delete" className="!text-[18px]" />
                    </button>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-on-surface-light dark:text-on-surface mb-1 group-hover:text-brand dark:group-hover:text-brand transition-colors">
                  {getOrgName(agent.org_id)}
                </h3>

                {agent.greeting_message && (
                  <p className="text-on-surface-light-variant dark:text-on-surface-variant text-sm mb-3 line-clamp-2">
                    {agent.greeting_message}
                  </p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-surface-light-highest dark:border-white/5">
                  <span className="text-xs text-on-surface-light-variant dark:text-on-surface-variant">
                    {t("agents.created")}{" "}
                    {agent.created_at
                      ? new Date(agent.created_at).toLocaleDateString(
                          i18n.language,
                        )
                      : "—"}
                  </span>
                  <button
                    onClick={() => openEditWizard(agent)}
                    className="text-xs text-brand font-medium hover:text-brand-light transition-colors"
                  >
                    {t("agents.edit")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {wizardOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div
            className="bg-surface-light-lowest dark:bg-surface-container border border-surface-light-highest dark:border-white/5 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-slideUp flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-brand dark:bg-brand-container p-5 text-white flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold">
                  {editingAgentId
                    ? t("agents.modal.editTitle")
                    : t("agents.modal.createTitle")}
                </h2>
                <p className="text-white/70 text-xs mt-0.5">
                  {t("agents.modal.stepIndicator", {
                    current: wizardStep + 1,
                    total: agentConfig.WIZARD_STEPS.length,
                    label: t(
                      `agents.steps.${agentConfig.WIZARD_STEPS[wizardStep].key}`,
                    ),
                  })}
                </p>
              </div>
              <button
                onClick={() => {
                  setWizardOpen(false);
                  resetWizard();
                }}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
              >
                <MIcon name="close" className="sm text-white" />
              </button>
            </div>

            {/* Stepper */}
            <div className="px-6 pt-6 flex-shrink-0">
              <WizardStepper
                steps={agentConfig.WIZARD_STEPS}
                currentStep={wizardStep}
              />
            </div>

            {/* Step Content */}
            <div className="px-6 pb-4 overflow-y-auto flex-1">
              {renderStepContent()}
              <MessageBanner message={wizardMsg} />
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-surface-light-highest dark:border-white/5 bg-surface-light-low dark:bg-surface-container-low flex items-center justify-between flex-shrink-0">
              <button
                onClick={handleBack}
                disabled={wizardStep === 0}
                className={`
                  px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-all
                  ${
                    wizardStep === 0
                      ? "opacity-0 pointer-events-none"
                      : "bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 text-on-surface-light dark:text-on-surface hover:bg-surface-light-high dark:hover:bg-surface-bright"
                  }
                `}
              >
                <MIcon name="arrow_back" className="sm" />
                <span>{t("agents.back")}</span>
              </button>

              {isLastStep ? (
                <button
                  onClick={handleCreateAgent}
                  disabled={saving || !canGoNext()}
                  className="px-5 py-2.5 bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white rounded-lg shadow-brand-glow transition-all font-semibold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                >
                  {saving ? (
                    <SpinnerIcon />
                  ) : (
                    <MIcon name="check_circle" className="sm" />
                  )}
                  <span>
                    {saving
                      ? t("agents.saving")
                      : editingAgentId
                        ? t("agents.saveChanges")
                        : t("agents.createAgentFull")}
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canGoNext()}
                  className="px-5 py-2.5 bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white rounded-lg shadow-brand-glow transition-all font-semibold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                >
                  <span>{t("agents.next")}</span>
                  <MIcon name="arrow_forward" className="sm" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ── */}
      {deleteTarget && (
        <DeleteConfirmModal
          agentOrgName={getOrgName(deleteTarget.org_id)}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default Agents;
